import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ECaseUtils} from '../eCaseUtils';
import {EcaseHttpService} from './ecase-http.service';
import {LoginAuthenticationService} from './login-authentication.service';
import {ECaseSnackBarService} from './snack-bar.service';

export const EDM_LIVE_DOC_TYPE_ID = {
  TEAM_MEMBER_CONSENT: 999999999999000,
  REVISIONS_IN_REVIEW: 999999999999001,
  APPLICATION_REVIEWS: 999999999999002,
  UNIQUE_REVIEWS_IN_REVIEW: 999999999999003,
  PRE_APPLICATION_REVIEWS: 999999999999004,
  APPLICATION_REVISIONS: 999999999999005,
  PA_REPORT: 999999999999006
} as const;

export const EDM_LIVE_DOCUMENT_TYPE_IDS: readonly number[] = [
  EDM_LIVE_DOC_TYPE_ID.TEAM_MEMBER_CONSENT,
  EDM_LIVE_DOC_TYPE_ID.REVISIONS_IN_REVIEW,
  EDM_LIVE_DOC_TYPE_ID.APPLICATION_REVIEWS,
  EDM_LIVE_DOC_TYPE_ID.UNIQUE_REVIEWS_IN_REVIEW,
  EDM_LIVE_DOC_TYPE_ID.PRE_APPLICATION_REVIEWS,
  EDM_LIVE_DOC_TYPE_ID.APPLICATION_REVISIONS,
  EDM_LIVE_DOC_TYPE_ID.PA_REPORT
];

export const EDM_OPENABLE_LIVE_DOC_TYPE_IDS: readonly number[] = EDM_LIVE_DOCUMENT_TYPE_IDS;

export interface LiveDocumentContext {
  applicationId: number;
  paReportId: number | null;
  documentName: string;
  destroy$: Observable<void>;
}

@Injectable({providedIn: 'root'})
export class EdmLiveDocumentService {
  constructor(
    private eCaseHttpService: EcaseHttpService,
    private loginAuthenticationService: LoginAuthenticationService,
    private eCaseSnackBarService: ECaseSnackBarService
  ) {
  }

  openLiveDocument(row: any, context: LiveDocumentContext): boolean {
    const baseUrl = this.loginAuthenticationService.baseUrl;

    switch (Number(row?.documentTypeId)) {
      case EDM_LIVE_DOC_TYPE_ID.TEAM_MEMBER_CONSENT:
        window.open(
          baseUrl + '/api/generatePdfApplicationAdmin/' + context.applicationId + '/' +
          row.attachedBySsoUserId + '/' + row.attachedByTeamMemberId + '/' +
          (row.formTypeId != null ? row.formTypeId : -1),
          '_blank'
        );
        return true;
      case EDM_LIVE_DOC_TYPE_ID.PA_REPORT:
        if (context.paReportId == null) {
          this.eCaseSnackBarService.show(
            'failure',
            'ecase.explorer.documentList.downloadDocumentIdMissing'
          );
          return true;
        }
        window.open(
          baseUrl + '/api/generatePdfForAdminPaReport/' + context.paReportId + '/' +
          row.attachedBySsoUserId,
          '_blank'
        );
        return true;
      case EDM_LIVE_DOC_TYPE_ID.REVISIONS_IN_REVIEW:
        window.open(
          baseUrl + '/api/generatePdfApplicationAdminRevision/' + context.applicationId + '/' +
          row.attachedBySsoUserId,
          '_blank'
        );
        return true;
      case EDM_LIVE_DOC_TYPE_ID.APPLICATION_REVIEWS:
      case EDM_LIVE_DOC_TYPE_ID.UNIQUE_REVIEWS_IN_REVIEW:
        window.open(
          baseUrl + '/api/generatePdfApplicationAdminReview/' + context.applicationId +
          '/' + row.contextObjectId,
          '_blank'
        );
        return true;
      case EDM_LIVE_DOC_TYPE_ID.PRE_APPLICATION_REVIEWS:
        window.open(
          baseUrl + '/api/generatePdfApplicationAdminReview/' +
          (row.contextCaseApplicationId || context.applicationId) + '/' +
          row.contextObjectId,
          '_blank'
        );
        return true;
      case EDM_LIVE_DOC_TYPE_ID.APPLICATION_REVISIONS:
        this.postRevision(row, context);
        return true;
      default:
        return false;
    }
  }

  private postRevision(row: any, context: LiveDocumentContext): void {
    this.eCaseHttpService.post('/api/generateArchivedPdfApplicationAdmin', {
      attachedBySsoUserId: row.attachedBySsoUserId,
      attachedByTeamMemberId: row.attachedByTeamMemberId,
      formTypeId: row.formTypeId,
      documentName: context.documentName,
      applicationId: Number(context.applicationId)
    }, {
      observe: 'response',
      responseType: 'blob'
    })
      .pipe(takeUntil(context.destroy$))
      .subscribe({
        next: (data: any): void => ECaseUtils.generateHyperLinkForDownload(data),
        error: (error: any): void => {
          console.log(error);
          this.eCaseSnackBarService.show('failure', error.toString());
        }
      });
  }
}
